/**
 * This file is used for any utility functions that are used for EHR orders,
 * order fulfillments, and order validation.
 */

import { RecipeBase } from "src/app/models/recipe.model";
import { Product } from "src/app/models/product.model";
import { StorageService } from "src/app/services/storage.service";
import { PatientOrderDisplayType, ProductType } from "src/app/app.enums";
import {
	IOrderFulfillment,
	IOrderFulfillmentContent,
	PatientEhrOrderModel,
} from "../models/patient.model";
import {
	FulfillmentValidationResult,
	IContentProduct,
	IOrderValidationResult2,
} from "../components/order-select/order-select.component";
import { IAdditive } from "../models/additive.model";
import { capitalize, getCaloricDensityTextFromOrder } from "../app.util";
import { CALORIE_TOLERANCE } from "../app.constants";

/**
 * True when a candidate calorie density matches the selected order's.
 *
 * Callers work in different units: the recipe calculators emit kcal/oz, milk bottles
 * carry kcal/mL. The order supplies both (calOz and calMl), so compare against each
 * rather than assuming the caller's unit — comparing a kcal/oz value against calMl
 * alone reports a mismatch on every recipe.
 *
 * Returns false when there is no order or no density to compare, matching the previous
 * arithmetic (NaN comparisons were always false). Callers that treat "no orders at all"
 * as a pass must still check that themselves.
 */
export const isOrderCalorieMatch = (
	order: PatientEhrOrderModel | null | undefined,
	calorieDensity: number | null | undefined,
): boolean => {
	if (!order || calorieDensity == null || Number.isNaN(calorieDensity)) {
		return false;
	}

	return (
		(order.calOz != null &&
			Math.abs(order.calOz - calorieDensity) < CALORIE_TOLERANCE) ||
		(order.calMl != null &&
			Math.abs(order.calMl - calorieDensity) < CALORIE_TOLERANCE)
	);
};

//--------------------------------------------------------------------------
// Shared Order Display Functions
//--------------------------------------------------------------------------

/**
 * Standing means the order is active (no end date).
 */
export const getStandingStatus = (order: PatientEhrOrderModel): string => {
	if (order.standingStatus) {
		return order.standingStatus;
	}

	if (!order.endDate) {
		return "Standing";
	}

	return "";
};

export const formatContents = (ehrOrder: PatientEhrOrderModel): string => {
	if (ehrOrder.contents) {
		return `${ehrOrder.base} ${getCaloricDensityTextFromOrder(
			ehrOrder
		)}: ${ehrOrder.contents}`;
	}

	return `${ehrOrder.base} ${getCaloricDensityTextFromOrder(ehrOrder)}`;
};

export const formatFeedingDuration = (
	ehrOrder: PatientEhrOrderModel
): string => {
	const route = capitalize(ehrOrder.route);
	const volumeWithUnit = ehrOrder.amount
		? `${ehrOrder.amount} ${ehrOrder.amountMeasuredUnit}`
		: "";

	// If route and volumeWithUnit have a value put a colon between them
	// if not, just display the one that has a value with no colon
	let routeVolumeDisplay = "";
	if (route && volumeWithUnit) {
		routeVolumeDisplay = `${route}: ${volumeWithUnit}`;
	} else {
		routeVolumeDisplay = route || volumeWithUnit;
	}

	if (ehrOrder.duration) {
		return `${routeVolumeDisplay}, over ${ehrOrder.duration}`;
	}

	return routeVolumeDisplay;
};

export const formatFrequency = (ehrOrder: PatientEhrOrderModel): string => {
	return `${ehrOrder.interval} ${ehrOrder.amountModifier ?? ""}`.trim();
};

/**
 * Returns an array of strings that are used to display the order details.
 * This includes the contents, feeding duration, and feeding interval.
 */
export const getOrderDetails = (order: PatientEhrOrderModel): string[] => [
	formatContents(order),
	formatFeedingDuration(order),
	formatFrequency(order),
	//`Cal: ${order.calMl}/mL (${Number(order.calOz.toFixed(4))}/oz)`,
];

//--------------------------------------------------------------------------
// Order Validation Functions
//--------------------------------------------------------------------------

const resolveProduct = (
	productId: Product["id"] | null | undefined
): Product | undefined =>
	productId == null
		? undefined
		: StorageService.configs.getProduct(String(productId));

const isHumanMilkBase = (product: Product | undefined): boolean =>
	// Must be a configured base so donor-derived fortifiers (e.g. HMF that is
	// isDonor/containsHumanMilk but not a base) are not treated as an equivalent base.
	Boolean(product?.isBase && (product.isEbm || product.isDonor));

/** The human milks an order's base allows, e.g. "EBM", "DM", "DBM", "EBM/DM + FORMULA". */
export interface AllowedHumanMilks {
	ebm: boolean;
	donor: boolean;
}

/** Splits an order base such as "EBM/DM + FORMULA" into its words. */
const ORDER_BASE_SEPARATOR = /[^A-Z]+/;

/**
 * Reads which human milks an order allows from its base, as the orders API returns it
 * ("EBM", "DM" or "DBM", "EBM/DM", each optionally "+ FORMULA"). No base allows none.
 */
export const getAllowedHumanMilks = (
	orderBase: string | null | undefined
): AllowedHumanMilks => {
	const tokens = (orderBase ?? "")
		.toUpperCase()
		.split(ORDER_BASE_SEPARATOR)
		.filter(Boolean);

	return {
		ebm: tokens.includes("EBM"),
		donor: tokens.includes("DM") || tokens.includes("DBM"),
	};
};

const isAllowedHumanMilk = (
	product: Product | undefined,
	allowed: AllowedHumanMilks
): boolean =>
	Boolean((product?.isEbm && allowed.ebm) || (product?.isDonor && allowed.donor));

/**
 * Whether the bottle's human-milk base can stand in for the order's. Only a milk the
 * order allows may (AEP-2452): an EBM/DM order whose fulfillment lists EBM as its only
 * base still takes DM (AEP-1833), but DM never passes an EBM-only order - which can mean
 * the family refused DM or it is contraindicated - and EBM never passes a DM-only order.
 */
const areEquivalentHumanMilkBases = (
	orderProduct: Product | undefined,
	bottleProduct: Product | undefined,
	allowed: AllowedHumanMilks
): boolean => {
	if (!isHumanMilkBase(orderProduct) || !isHumanMilkBase(bottleProduct)) {
		return false;
	}

	if (!isAllowedHumanMilk(bottleProduct, allowed)) {
		return false;
	}

	// Fail-closed on missing density.
	const densityA = orderProduct?.calorieDensity;
	const densityB = bottleProduct?.calorieDensity;
	if (densityA == null || densityB == null) {
		return false;
	}

	return densityA === densityB;
};

const isWaterBase = (product: Product | undefined): boolean =>
	Boolean(product?.isBase && product.type === ProductType.Water);

/**
 * Bases that can stand in for each other in an order's base slot: EBM and DM of
 * the same caloric density when the order allows the bottle's milk, or any two
 * waters. A formula order stores a single water product as its base
 * (HL7Controller.HandleBases picks one of the unit's waters), so a branded water -
 * Similac or Enfamil Sterile Water - must match it too (AEP-2306). Human milk and
 * water never stand in for each other.
 */
const areEquivalentBases = (
	orderProduct: Product | undefined,
	bottleProduct: Product | undefined,
	allowed: AllowedHumanMilks
): boolean =>
	areEquivalentHumanMilkBases(orderProduct, bottleProduct, allowed) ||
	(isWaterBase(orderProduct) && isWaterBase(bottleProduct));

/**
 * Returns an array of all the fortifiers that are missing from the order
 * fulfillment.
 *
 * @param contents is a list of the selected/desired contents to check against
 * the selected fulfillment option.
 *
 * @param fulfillmentContents is a list of all the contents that the selected
 * contents are being checked against.
 */
export const findMissingFortifiers = (
	contents: IContentProduct[],
	fulfillmentContents: IOrderFulfillmentContent[]
): IContentProduct[] => {
	// Format the fulfillment contents to match the selected contents and remove duplicate product ids
	const fulfillmentFortifiers = fulfillmentContents
		.filter((o) => o.isBase === false)
		.map(
			(c) =>
				({
					productId: c.productId,
					productName: c.productName,
				}) as IContentProduct
		);

	// Return only the fortifiers that are not in the selected contents and remove duplicates
	return fulfillmentFortifiers
		.filter((f) => !contents.some((c) => c.productId === f.productId))
		.filter((f, index, arr) => arr.indexOf(f) === index);
};

/**
 * Gets the base products from the order fulfillment and checks if any of them are in the milk bottle.
 *
 * @param products
 * @param fulfillmentBases
 */
export const hasFulfillmentBase = (
	products: IContentProduct[],
	fulfillmentContents: IOrderFulfillmentContent[],
	orderBase?: string | null
): boolean => {
	const allowed = getAllowedHumanMilks(orderBase);
	const baseProducts = fulfillmentContents
		.filter((content) => content.isBase)
		.map((content) => ({
			productId: content.productId,
			product: resolveProduct(content.productId),
		}));

	return products.some((p) => {
		const pProduct = resolveProduct(p.productId);
		return baseProducts.some(
			(base) =>
				base.productId === p.productId ||
				// Waters stand in for each other, and so do EBM and DM the order allows.
				areEquivalentBases(base.product, pProduct, allowed)
		);
	});
};

/**
 * This returns true if there are no bases in the order fulfillment or if there is at least one base in the milk bottle.
 *
 * @param products
 * @param fulfillmentContents
 */
export const hasOptionalFulfillmentBase = (
	products: IContentProduct[],
	fulfillmentContents: IOrderFulfillmentContent[],
	orderBase?: string | null
): boolean => {
	const bases = fulfillmentContents.filter((content) => content.isBase);
	return (
		bases.length === 0 ||
		hasFulfillmentBase(products, fulfillmentContents, orderBase)
	);
};

/**
 * Returns true if base is in the order fulfillment.
 */
export const isBaseInOrderFulfillment = (
	recipeBase: RecipeBase,
	selectedFulfillment: IOrderFulfillment
): boolean => {
	const bases = selectedFulfillment.contents.filter(
		(content) => content.isBase
	);
	return bases.some((base) => base.productId === recipeBase.baseProductId);
};

/**
 * Package up various validation results into a single object.
 *
 * @param products are the list of products that are in the assigned feed.
 * @param fulfillment is the selected order fulfillment.
 */
export const validateContentProducts = (
	products: IContentProduct[],
	fulfillment: IOrderFulfillment,
	orderBase?: string | null
): FulfillmentValidationResult => {
	const contents: IOrderFulfillmentContent[] = fulfillment.contents.filter(
		(o) => o.productId !== null
	);

	const fulfillmentId = fulfillment.id;
	const extraProducts = findExtraProducts(products, contents, orderBase);
	const hasBase = hasOptionalFulfillmentBase(products, contents, orderBase);
	const missingFortifiers = findMissingFortifiers(products, contents);

	// In order to be valid, need to have a base, no missing fortifiers, and no extra products.
	const hasAllFortifiers = !missingFortifiers.length;
	const hasExtraProducts = !!extraProducts.length;
	const isValid = hasValidContentProducts(
		hasBase,
		hasAllFortifiers,
		hasExtraProducts
	);

	return {
		ehrOrderFulfillmentId: fulfillmentId,
		milkBottleContentItemsNotInOrderFulfillment: extraProducts,
		baseOrderedIsPresent: hasBase,
		orderFulfillmentNonBaseProductsNotInBottle: missingFortifiers,
		isValid,
	};
};

/**
 * Returns true if the order fulfillment has a base and all fortifiers as
 * well as no extra products.
 *
 * @param hasBase
 * @param hasAllFortifiers
 * @param hasExtraProducts
 */
export const hasValidContentProducts = (
	hasBase: boolean,
	hasAllFortifiers: boolean,
	hasExtraProducts: boolean
): boolean => hasBase && hasAllFortifiers && !hasExtraProducts;

/**
 * Returns an array of all unique products (both bases and fortifiers) that are
 * not in the order fulfillment.
 *
 * This is used to determine if there are any extra products in the feed that
 * are not supposed to be in the order fulfillment.
 *
 * Example:
 * - Order fulfillment has water and gentlease.
 * - Selected products include water (base) and Prolacta.
 * - This function will return Prolacta because it is not supposed to be in the
 * 	 order fulfillment.
 *
 * @param contents
 * @param fulfillmentContents
 */
export const findExtraProducts = (
	contents: IContentProduct[],
	fulfillmentContents: IOrderFulfillmentContent[],
	orderBase?: string | null
): IContentProduct[] => {
	const allowed = getAllowedHumanMilks(orderBase);
	const fulfillmentContentProducts: IContentProduct[] =
		fulfillmentContents.map((c) => ({
			productId: c.productId,
			productName: c.productName,
		}));

	// A base in the bottle should not count as an "extra" product when the order
	// specifies an equivalent base: any water stands in for the order's water, and EBM
	// or DM of the same caloric density when the order's base allows that milk.
	const substitutableBaseProducts = fulfillmentContents
		.filter((c) => c.isBase)
		.map((c) => resolveProduct(c.productId))
		.filter((p) => isHumanMilkBase(p) || isWaterBase(p));

	// Return only the product contents that are not in the selected contents and remove duplicates
	return contents.filter((f) => {
		if (fulfillmentContentProducts.some((c) => c.productId === f.productId)) {
			return false;
		}

		const fProduct = resolveProduct(f.productId);
		return !substitutableBaseProducts.some((base) =>
			areEquivalentBases(base, fProduct, allowed)
		);
	});
};

export const hasMissingValue = (
	selectedOrder: PatientEhrOrderModel,
	calorieDensity: number,
	additives: IAdditive[]
): boolean => {
	if (!selectedOrder || !calorieDensity || !additives.length) {
		console.warn("One or more params are null");
		console.table({
			selectedOrder,
			calorieDensity,
			additives,
		});
		return false;
	}
	return true;
};

export const createContentProductsList = (
	additives: IAdditive[],
	recipeBase: RecipeBase
): IContentProduct[] => {
	const products = additives
		.filter((a) => a.productId)
		.map((a) => ({
			productId: a.productId,
			productName: a.productName,
		}))
		.concat({
			productId: recipeBase.baseProductId,
			productName: recipeBase.baseName,
		});

	return products;
};

export const isMilkBottleOrderMatch = (
	milkBottleId: string,
	results: IOrderValidationResult2[]
): boolean => {
	if (!results) {
		return;
	}

	const result = results.find((v) => v.milkBottleId === milkBottleId);
	if (!result) {
		console.warn(
			`order-select.component: milkBottle not found: ${milkBottleId}`
		);
	}
	return result.isValid;
};

/**
 * Returns the validation status if the scanned item matches the order validation results
 *
 * TODO: if the scanned item is not found, should we throw an error?
 *
 * @param scannedItemKey
 * @param validationResults
 */
export const isScannedItemOrderMatch = (
	scannedItemKey: string,
	validationResults: IOrderValidationResult2[]
): boolean => {
	if (!validationResults) {
		return;
	}

	const result = validationResults.find((v) => v.key === scannedItemKey);
	if (!result) {
		console.warn(
			`order-match.component: scannedItem not found: scannedItemKey = ${scannedItemKey}`
		);
	}
	return result.isValid;
};

export function isFormulaBase(base: string | undefined): boolean {
	return base?.toUpperCase() === PatientOrderDisplayType.FORMULA;
}

